'use client'

import PropTypes from 'prop-types'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUITypography from '@mui/material/Typography'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUIAlert from '@mui/material/Alert'
import { LargeMatterSuccessIcon } from '@repo/ui/LargeMatterSuccessIcon'
import { LargeMatterFailureIcon } from '@repo/ui/LargeMatterFailureIcon'
import { LargeSuccessIcon } from '@repo/ui/LargeSuccessIcon'
import { useCOISStore } from '@repo/store/useCOISStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useWizardStore } from '@repo/store/useWizardStore'
import { LoadingState } from '@repo/ui/LoadingState'
import { useState, useCallback } from 'react'
import { NewClientNumberEntry } from '@repo/ui/NewClientNumberEntry'
import { NewMatterNumberEntry } from '@repo/ui/NewMatterNumberEntry'
import { NewUFNEntry } from '@repo/ui/NewUFNEntry'

export const SubmitCOISClientMatterWizard = ({ wizardId, testMode = false, onStatusChange }) => {
  const {
    newClientNo,
    existingClient,
    namedClients,
    updateState: updateClientState,
    isNew: clientIsNew,
    reset: resetNewClientStore
  } = useNewClientStore()
  const {
    saveNewMatterWizard,
    newMatterNo,
    updateState: updateMatterState,
    uniqueFileNoDate,
    reset: resetNewMatterStore
  } = useNewMatterStore()
  const { setWizardIsComplete } = useWizardStore()
  const { reset: resetCOISStore } = useCOISStore()
  const [loadingStatus, setLoadingStatus] = useState(false)

  const [errorStatus, setErrorStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [errorFields, setErrorFields] = useState([])

  const [hasError, setHasError] = useState(false)
  const [showClientNoEntry, setShowClientNoEntry] = useState(false)
  const [showMatterNoEntry, setShowMatterNoEntry] = useState(false)
  const [showUfnEntry, setShowUfnEntry] = useState(false)
  const [showGeneralError, setShowGeneralError] = useState(false)
  const surname = namedClients ? namedClients[0]?.surname : ''

  const saveWizard = useCallback(async () => {
    if (loadingStatus || testMode) return

    if (onStatusChange) onStatusChange(null)
    setShowClientNoEntry(false)
    setShowMatterNoEntry(false)
    setShowUfnEntry(false)
    setShowGeneralError(false)
    setHasError(false)
    setLoadingStatus('loading')
    setErrorStatus(null)
    setErrorMessage('')

    try {
      await saveNewMatterWizard({ wizardId })
      // read the saved numbers from live store state, not this callback's closure — the closure holds
      // the values from first render, which are stale after a "number already exists" retry (the retry
      // entry fields write the replacement number to the stores). Computed before the resets below so
      // nothing here can fail after the wizard state is destroyed.
      const savedClient = useNewClientStore.getState()
      const savedMatter = useNewMatterStore.getState()
      const clientNo = savedClient.isNew ? savedClient.newClientNo : savedClient.existingClient?.clientNo
      const matterNo = savedMatter.isNew ? savedMatter.newMatterNo : savedMatter.existingMatter?.matterNo
      setLoadingStatus('success')
      resetCOISStore()
      resetNewClientStore()
      resetNewMatterStore()
      setWizardIsComplete(true)
      if (onStatusChange) onStatusChange(`${clientNo}-${matterNo}`)
    } catch (err) {
      console.log(err)
      setHasError(true)
      setWizardIsComplete(false)
      if (onStatusChange) onStatusChange('error')

      const errorKeysToIgnore = [
        'createClientMatterAndConflictOfInterestRequest',
        'createClientAndMatterRequest',
        'createMatterRequest'
      ]

      const camelToFlat = (c) => ((c = c.replace(/[A-Z]/g, ' $&')), c[0].toUpperCase() + c.slice(1).toLowerCase())
      const errorKeys = Object.keys(err?.response?.data?.errors || {})
        .filter((key) => {
          return !errorKeysToIgnore.includes(key)
        })
        .map((key) => {
          return key
            .split('.')
            .filter((part) => part !== '$')
            .map(camelToFlat)
            .join(' / ')
        })

      // there's an edge case where the client/matter/ufns are not unique by the time the user saves. So we give the user the change to get/set a new one
      switch (err) {
        case 'Client number already exists':
          setShowClientNoEntry(true)
          break
        case 'Matter number already exists':
          setShowMatterNoEntry(true)
          break
        case 'Unique file number already exists':
          setShowUfnEntry(true)
          break
        default:
          setShowGeneralError(true)
          setErrorMessage(err?.response?.data?.title || err?.message || '')
          setErrorStatus(err?.status || null)
          setErrorFields(errorKeys || [])
          break
      }

      setLoadingStatus('error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getTitle = () => {
    if (!loadingStatus) return 'Last step!'
    if (loadingStatus === 'loading') return 'Saving...'
    if (loadingStatus === 'success') return 'Success!'
    if (loadingStatus === 'error') return 'Error'
  }

  const getText = () => {
    if (!loadingStatus)
      return 'When you are happy with everything, please click the button below to submit your details.'
    if (loadingStatus === 'loading') return 'Please be patient, as this can take a few seconds.'
    if (loadingStatus === 'success')
      return `Your details have successfully been created and saved to the Matter History. Click 'Finish' below to go to the new matter dashboard.`

    if (loadingStatus === 'error') {
      if (showClientNoEntry)
        return 'The supplied Client No is no longer unique. Please review below and click the button to try again.'
      if (showMatterNoEntry)
        return 'The supplied Matter No is no longer unique. Please review below and click the button to try again.'
      if (showUfnEntry)
        return 'The supplied Unique File No is no longer unique. Please review below and click the button to try again.'

      return 'Unfortunately, there were errors during saving. Please see details below.'
    }
  }

  return (
    <>
      <WizardPanel data-testid="SubmitCOISClientMatterWizard">
        <MUIStack
          direction="column"
          gap={4}
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: '400px', maxWidth: '500px', margin: '0 auto' }}
        >
          {!loadingStatus ? <LargeSuccessIcon sx={{ width: '90px', height: '90px', mb: '32px' }} /> : null}

          {loadingStatus === 'loading' ? <LoadingState sx={{ width: '90px', height: '90px', mb: '32px' }} /> : null}

          {loadingStatus === 'success' ? (
            <LargeMatterSuccessIcon sx={{ width: '90px', height: '90px', mb: '32px' }} />
          ) : null}

          {loadingStatus === 'error' ? (
            <LargeMatterFailureIcon sx={{ width: '90px', height: '90px', mb: '32px' }} />
          ) : null}

          <MUITypography variant="h4" sx={{ mb: 0 }}>
            {getTitle()}
          </MUITypography>

          <MUITypography variant="body1" sx={{ textAlign: 'center' }}>
            {getText()}
          </MUITypography>

          {!loadingStatus ? (
            <MUIButton variant="contained" sx={{ mt: 4, height: '53px' }} onClick={saveWizard}>
              Submit
            </MUIButton>
          ) : null}

          {hasError && (showClientNoEntry || showMatterNoEntry || showUfnEntry) ? (
            <MUIStack direction="row" gap={4} justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
              {showClientNoEntry ? (
                <NewClientNumberEntry
                  defaultValue={newClientNo || ''}
                  onChange={(value) => updateClientState({ key: 'newClientNo', value })}
                  surname={surname}
                  size="medium"
                  sx={{ width: '240px', mt: 4 }}
                  testMode={testMode}
                />
              ) : null}

              {showMatterNoEntry ? (
                <NewMatterNumberEntry
                  defaultValue={newMatterNo || ''}
                  onChange={(value) => updateMatterState({ key: 'newMatterNo', value })}
                  clientNo={clientIsNew ? newClientNo : existingClient.clientNo || ''}
                  size="medium"
                  sx={{ width: '240px', mt: 4 }}
                  testMode={testMode}
                />
              ) : null}

              {showUfnEntry ? (
                <NewUFNEntry
                  date={uniqueFileNoDate}
                  onChange={(value) => updateMatterState({ key: 'uniqueFileNo', value })}
                  size="medium"
                  sx={{ width: '240px', mt: 4 }}
                  testMode={testMode}
                  clientNo={newClientNo || existingClient?.clientNo || null}
                />
              ) : null}

              {!showGeneralError ? (
                <MUIButton variant="contained" sx={{ mt: 0, height: '53px' }} onClick={saveWizard}>
                  Try again
                </MUIButton>
              ) : null}
            </MUIStack>
          ) : null}

          {showGeneralError && errorMessage ? (
            <MUIAlert severity="error" sx={{ width: '100%' }}>
              <MUITypography variant="h6" sx={{ color: 'error.dark' }}>
                {`Error${errorStatus ? `: ${errorStatus}` : ''}`}
              </MUITypography>

              {errorMessage ? (
                <MUITypography variant="body1">
                  {errorMessage}
                  {errorFields?.length ? ' Please check the following fields:' : ''}
                </MUITypography>
              ) : null}

              {errorFields?.length ? (
                <>
                  <MUITypography variant="body1" component="ul" sx={{ mt: 4 }}>
                    {errorFields.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </MUITypography>
                </>
              ) : null}
            </MUIAlert>
          ) : null}
        </MUIStack>
      </WizardPanel>
    </>
  )
}

SubmitCOISClientMatterWizard.propTypes = {
  wizardId: PropTypes.string,
  testMode: PropTypes.bool,
  onStatusChange: PropTypes.func
}
